/**
 * A trip's fields — the one way InOut asks for a trip: when it began, when it
 * ended, where to. Every form that holds a trip renders these: an absence
 * written down or corrected, the calculator's trips, a trip planned and the
 * "what if I travel?" question.
 *
 *   `past`    — a trip made: up to today, years back; no return yet is still
 *               away, and the destination is optional and says why it helps.
 *   `planned` — a trip ahead: from today, a few years on.
 *
 * Each date's refusal is said under it and is its description (FieldError);
 * the return cannot be picked before the departure. The form's schema owns the
 * rules — these fields only say its refusals.
 */
import { useCountries, useLocale } from '@tonnie/locale';
import { CountrySelect, DateField, FieldError, Label, cn, describedBy } from '@tonnie/ui';
import {
  useController,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import { CALENDAR_YEARS, deviceToday, deviceYear } from '../../lib/local-today';

/** How far ahead a trip to plan or to simulate can be picked. */
export const PLAN_YEARS_AHEAD = 5;

export type InOutTripWhen = 'past' | 'planned';

interface InOutTripFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** The form's names for the departure, the return and the destination. */
  names: { from: FieldPath<T>; to: FieldPath<T>; destination: FieldPath<T> };
  /** The fields' ids start with it: `<prefix>-departed`, `<prefix>-departs`, … */
  idPrefix: string;
  when: InOutTripWhen;
  className?: string;
}

/** The fields' id suffixes, as each kind of trip has always named them. */
const IDS = {
  past: { from: 'departed', to: 'returned' },
  planned: { from: 'departs', to: 'returns' },
} as const;

export function InOutTripFields<T extends FieldValues>({
  control,
  names,
  idPrefix,
  when,
  className,
}: InOutTripFieldsProps<T>) {
  const { t, intlLocale } = useLocale();
  const { countries } = useCountries();
  const from = useController({ control, name: names.from });
  const to = useController({ control, name: names.to });
  const destination = useController({ control, name: names.destination });
  const departure = useWatch({ control, name: names.from }) as string | undefined;

  const today = deviceToday();
  const year = deviceYear();
  const past = when === 'past';
  const years = past
    ? { fromYear: year - CALENDAR_YEARS, toYear: year }
    : { fromYear: year, toYear: year + PLAN_YEARS_AHEAD };
  const ids = {
    from: `${idPrefix}-${IDS[when].from}`,
    to: `${idPrefix}-${IDS[when].to}`,
    toHint: `${idPrefix}-${IDS[when].to}-hint`,
    destination: `${idPrefix}-destination`,
  };
  const fromError = from.fieldState.error;
  const toError = to.fieldState.error;

  return (
    <div className={cn('@container flex flex-col gap-4', className)} data-slot="inout-trip-fields">
      <div className="grid gap-3 @md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.from}>
            {past ? t('inout.absence.departedOn') : t('inout.planner.departsOn')}
          </Label>
          <DateField
            id={ids.from}
            value={(from.field.value as string | undefined) ?? ''}
            onChange={from.field.onChange}
            intlLocale={intlLocale}
            calendarLabel={t('inout.form.pickDate')}
            clearLabel={t('common.clear')}
            {...(past ? { max: today } : { min: today })}
            {...years}
            invalid={!!fromError}
            describedBy={describedBy(fromError && `${ids.from}-error`)}
          />
          <FieldError id={`${ids.from}-error`}>
            {fromError ? t('inout.form.dateInvalid') : null}
          </FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.to}>
            {past ? t('inout.absence.returnedOn') : t('inout.planner.returnsOn')}
          </Label>
          <DateField
            id={ids.to}
            value={(to.field.value as string | null | undefined) ?? ''}
            onChange={to.field.onChange}
            intlLocale={intlLocale}
            calendarLabel={t('inout.form.pickDate')}
            clearLabel={t('common.clear')}
            {...(past ? { max: today } : { min: today })}
            calendarMin={departure || undefined}
            {...years}
            invalid={!!toError}
            describedBy={describedBy(past && ids.toHint, toError && `${ids.to}-error`)}
          />
          <FieldError id={`${ids.to}-error`}>
            {toError
              ? // The order refinement is the schemas' only `custom` issue.
                toError.type === 'custom'
                ? t('inout.form.returnBeforeDeparture')
                : t('inout.form.dateInvalid')
              : null}
          </FieldError>
        </div>
      </div>
      {past ? (
        <p id={ids.toHint} className="-mt-2 text-xs text-muted-foreground">
          {t('inout.absence.stillAwayHint')}
        </p>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={ids.destination}>
          {past ? t('inout.absence.destination') : t('inout.planner.destination')}
        </Label>
        <CountrySelect
          id={ids.destination}
          countries={countries}
          value={(destination.field.value as string | null | undefined) ?? ''}
          onValueChange={destination.field.onChange}
          placeholder={t('inout.absence.destinationPlaceholder')}
          searchPlaceholder={t('common.searchCountry')}
          emptyMessage={t('common.noCountryFound')}
        />
        {past ? (
          <p className="text-xs text-muted-foreground">{t('inout.absence.destinationHint')}</p>
        ) : null}
      </div>
    </div>
  );
}
